/**
 * Heuristic Engine — Client-side rep detection from joint angles
 *
 * Takes a heuristic_rule (from the kinetic tool's ai_tools.heuristic_rule)
 * and evaluates it against live MediaPipe landmarks to detect reps.
 *
 * A rep is a full cycle: the target joint crosses BELOW the threshold (down),
 * then returns ABOVE it (up). This prevents double-counting.
 *
 * The engine is stateful — it tracks the current phase of the movement
 * and emits rep events only on complete cycles.
 */

import { angleBetweenPoints, LANDMARK, type NormalizedLandmark } from './mediapipe-pose';

// ─── Types ──────────────────────────────────────────────────────

export interface HeuristicRule {
  target_metric: 'repetition' | 'duration' | 'distance';
  joint_angles: string[];         // e.g., ['shoulder', 'elbow', 'wrist']
  success_threshold_degrees: string; // e.g., '< 90' or '> 160'
  min_confidence: number;         // e.g., 0.85
  return_threshold_degrees?: string; // e.g., '> 150' — where the limb must return to count as "up"
  cooldown_ms?: number;           // minimum ms between reps (prevents double-counting)
}

export interface RepEvent {
  type: 'rep_completed';
  timestamp_ms: number;
  confidence_score: number;
  joint_data: {
    angles: Record<string, number>;
    min_angle_reached: number;
  };
}

// ─── Joint name → landmark index triplets ───────────────────────

/** Maps a joint name to the three landmark indices needed to compute its angle */
const JOINT_TRIPLETS: Record<string, [number, number, number]> = {
  // Upper body
  left_elbow:    [LANDMARK.LEFT_SHOULDER,  LANDMARK.LEFT_ELBOW,  LANDMARK.LEFT_WRIST],
  right_elbow:   [LANDMARK.RIGHT_SHOULDER, LANDMARK.RIGHT_ELBOW, LANDMARK.RIGHT_WRIST],
  left_shoulder: [LANDMARK.LEFT_ELBOW,     LANDMARK.LEFT_SHOULDER, LANDMARK.LEFT_HIP],
  right_shoulder:[LANDMARK.RIGHT_ELBOW,    LANDMARK.RIGHT_SHOULDER, LANDMARK.RIGHT_HIP],

  // Lower body
  left_knee:     [LANDMARK.LEFT_HIP,   LANDMARK.LEFT_KNEE,  LANDMARK.LEFT_ANKLE],
  right_knee:    [LANDMARK.RIGHT_HIP,  LANDMARK.RIGHT_KNEE, LANDMARK.RIGHT_ANKLE],
  left_hip:      [LANDMARK.LEFT_SHOULDER, LANDMARK.LEFT_HIP, LANDMARK.LEFT_KNEE],
  right_hip:     [LANDMARK.RIGHT_SHOULDER, LANDMARK.RIGHT_HIP, LANDMARK.RIGHT_KNEE],

  // Convenience aliases (average both sides)
  elbow:    [LANDMARK.LEFT_SHOULDER,  LANDMARK.LEFT_ELBOW,  LANDMARK.LEFT_WRIST],
  shoulder: [LANDMARK.LEFT_ELBOW,     LANDMARK.LEFT_SHOULDER, LANDMARK.LEFT_HIP],
  knee:     [LANDMARK.LEFT_HIP,       LANDMARK.LEFT_KNEE,    LANDMARK.LEFT_ANKLE],
  hip:      [LANDMARK.LEFT_SHOULDER,  LANDMARK.LEFT_HIP,     LANDMARK.LEFT_KNEE],
  wrist:    [LANDMARK.LEFT_ELBOW,     LANDMARK.LEFT_WRIST,   LANDMARK.LEFT_INDEX],
};

/** For aliases, also compute the right side and average */
const BILATERAL_JOINTS = new Set(['elbow', 'shoulder', 'knee', 'hip', 'wrist']);

// ─── Threshold parsing ──────────────────────────────────────────

type Comparator = '<' | '>' | '<=' | '>=';

function parseThreshold(expr: string): { comparator: Comparator; value: number } {
  const match = expr.trim().match(/^(<|>|<=|>=)\s*(\d+(?:\.\d+)?)$/);
  if (!match) throw new Error(`Invalid threshold: ${expr}`);
  return { comparator: match[1] as Comparator, value: parseFloat(match[2]) };
}

function meetsThreshold(angle: number, comparator: Comparator, value: number): boolean {
  switch (comparator) {
    case '<': return angle < value;
    case '>': return angle > value;
    case '<=': return angle <= value;
    case '>=': return angle >= value;
  }
}

// ─── Engine class ───────────────────────────────────────────────

/**
 * Two-zone state machine with hysteresis and cooldown.
 *
 * A rep requires:
 *   1. Elbow angle drops BELOW the "down" threshold (e.g., < 90°) → enters DOWN zone
 *   2. Elbow angle rises ABOVE the "return" threshold (e.g., > 150°) → enters UP zone → REP COUNTED
 *   3. Cooldown period before next rep can be counted (prevents jitter double-counts)
 *
 * The two-zone approach with a wide dead zone in between (90-150°) means:
 *   - Holding at the bottom doesn't trigger multiple reps (you're still in DOWN zone)
 *   - Slight wobbles during the motion don't trigger reps (you're in the dead zone)
 *   - You must fully extend back to the top to count the rep
 */
export class HeuristicEngine {
  private rule: HeuristicRule;
  private downThreshold: { comparator: Comparator; value: number };
  private returnThreshold: { comparator: Comparator; value: number };
  private cooldownMs: number;
  private phase: 'waiting' | 'down' = 'waiting';
  private extremeAngleReached = 0;
  private repCount = 0;
  private tracksMax: boolean;
  private lastRepTime = 0;

  constructor(rule: HeuristicRule) {
    this.rule = rule;
    this.downThreshold = parseThreshold(rule.success_threshold_degrees);
    this.tracksMax = this.downThreshold.comparator === '>' || this.downThreshold.comparator === '>=';

    // Return threshold: where the limb must return to count as "back up"
    // If not specified, auto-compute: for "< 90" down → "> 150" return (wide hysteresis)
    if (rule.return_threshold_degrees) {
      this.returnThreshold = parseThreshold(rule.return_threshold_degrees);
    } else {
      // Auto-compute return threshold with ~60° hysteresis band
      if (this.tracksMax) {
        // For "> 150" (jumping jacks up), return is "< 90" (arms down)
        this.returnThreshold = { comparator: '<', value: Math.max(this.downThreshold.value - 60, 30) };
      } else {
        // For "< 90" (pushup down), return is "> 150" (arms extended)
        this.returnThreshold = { comparator: '>', value: Math.min(this.downThreshold.value + 60, 170) };
      }
    }

    this.cooldownMs = rule.cooldown_ms ?? 800; // minimum 800ms between reps (~75 reps/min max)
    this.extremeAngleReached = this.tracksMax ? 0 : 180;
  }

  /**
   * Evaluate a frame of landmarks against the heuristic rule.
   * Returns a RepEvent if a complete rep was detected, null otherwise.
   */
  evaluate(landmarks: NormalizedLandmark[]): RepEvent | null {
    if (!landmarks || landmarks.length < 33) return null;

    // Compute angles for all joints in the rule
    const angles: Record<string, number> = {};
    let totalConfidence = 0;
    let jointCount = 0;

    for (const jointName of this.rule.joint_angles) {
      const triplet = JOINT_TRIPLETS[jointName];
      if (!triplet) continue;

      if (BILATERAL_JOINTS.has(jointName)) {
        const leftTriplet = triplet;
        const rightKey = `right_${jointName}`;
        const rightTriplet = JOINT_TRIPLETS[rightKey];

        const leftAngle = angleBetweenPoints(
          landmarks[leftTriplet[0]], landmarks[leftTriplet[1]], landmarks[leftTriplet[2]]
        );

        if (rightTriplet) {
          const rightAngle = angleBetweenPoints(
            landmarks[rightTriplet[0]], landmarks[rightTriplet[1]], landmarks[rightTriplet[2]]
          );
          angles[jointName] = (leftAngle + rightAngle) / 2;
        } else {
          angles[jointName] = leftAngle;
        }

        // Confidence from visibility — use both sides for bilateral
        const leftVis = leftTriplet.map((i) => landmarks[i]?.visibility ?? 0);
        const rightVis = rightTriplet ? rightTriplet.map((i) => landmarks[i]?.visibility ?? 0) : leftVis;
        const allVis = [...leftVis, ...rightVis];
        totalConfidence += allVis.reduce((a, b) => a + b, 0) / allVis.length;
      } else {
        angles[jointName] = angleBetweenPoints(
          landmarks[triplet[0]], landmarks[triplet[1]], landmarks[triplet[2]]
        );

        const vis = triplet.map((i) => landmarks[i]?.visibility ?? 0);
        totalConfidence += vis.reduce((a, b) => a + b, 0) / vis.length;
      }

      jointCount++;
    }

    if (jointCount === 0) return null;

    const avgConf = totalConfidence / jointCount;
    if (avgConf < this.rule.min_confidence) return null;

    const primaryJoint = this.rule.joint_angles[0];
    const primaryAngle = angles[primaryJoint] ?? 0;

    // Track extreme angle during the active phase
    if (this.tracksMax) {
      if (primaryAngle > this.extremeAngleReached) this.extremeAngleReached = primaryAngle;
    } else {
      if (primaryAngle < this.extremeAngleReached) this.extremeAngleReached = primaryAngle;
    }

    // ─── Two-zone state machine ─────────────────────────────────
    const now = Date.now();
    const downMet = meetsThreshold(primaryAngle, this.downThreshold.comparator, this.downThreshold.value);
    const returnMet = meetsThreshold(primaryAngle, this.returnThreshold.comparator, this.returnThreshold.value);

    if (this.phase === 'waiting' && downMet) {
      // Entered the DOWN zone (e.g., elbows bent past 90°)
      this.phase = 'down';
      return null;
    }

    if (this.phase === 'down' && returnMet) {
      // Returned to the UP zone (e.g., elbows extended past 150°) — REP COMPLETE

      // Enforce cooldown to prevent jitter double-counts
      if (now - this.lastRepTime < this.cooldownMs) {
        return null;
      }

      this.phase = 'waiting';
      this.repCount++;
      this.lastRepTime = now;

      const event: RepEvent = {
        type: 'rep_completed',
        timestamp_ms: now,
        confidence_score: Math.min(avgConf, 1.0),
        joint_data: {
          angles: { ...angles },
          min_angle_reached: this.extremeAngleReached,
        },
      };

      this.extremeAngleReached = this.tracksMax ? 0 : 180;
      return event;
    }

    return null;
  }

  /** Reset engine state */
  reset(): void {
    this.phase = 'waiting';
    this.extremeAngleReached = this.tracksMax ? 0 : 180;
    this.repCount = 0;
    this.lastRepTime = 0;
  }

  get reps(): number {
    return this.repCount;
  }

  /** Current phase of the state machine */
  get currentPhase(): 'waiting' | 'down' {
    return this.phase;
  }
}
